<div class="reports-page">
  <header class="page-header">
    <div>
      <h1>Scheduled Reports</h1>
      <p class="subtitle">
        Create recurring canned or custom flat-file reports, preview them before activation, and manage run history.
      </p>
    </div>

    @if (viewMode !== 'list') {
      <div class="header-actions">
        @if (viewMode === 'detail') {
          <button mat-button type="button" (click)="backToReportList()">Back to reports</button>
        }
        @if (viewMode === 'create' || viewMode === 'edit') {
          <button mat-button type="button" (click)="cancelEditor()">
            {{ detailReport && editingReportId ? 'Back to detail' : 'Back to list' }}
          </button>
        }
      </div>
    }
  </header>

  @if (isCompanyStateLoading || isBootstrapping) {
    <section class="page-loading">
      <mat-spinner [diameter]="32"></mat-spinner>
      <span>Loading scheduled reports</span>
    </section>
  }

  @if (!isCompanyStateLoading && !isBootstrapping) {
    @if (viewMode === 'list') {
      <section class="list-shell">
        <div class="report-type-tabs">
          @for (tab of reportTypeTabs; track tab) {
            <div
              class="report-type-tab"
              [class.active]="activeListTab === tab.value"
              [class.can-create]="currentPermissions.create"
              role="button"
              tabindex="0"
              (click)="setListTab(tab.value)"
              (keydown.enter)="setListTab(tab.value)"
              (keydown.space)="setListTab(tab.value); $event.preventDefault()"
              >
              <div class="report-type-copy">
                <span>{{ tab.label }}</span>
                <small>{{ tab.description }}</small>
              </div>
              @if (currentPermissions.create) {
                <div class="report-type-actions">
                  <button
                    mat-stroked-button
                    type="button"
                    (click)="startCreate(tab.value); $event.stopPropagation()"
                    >
                    Create
                  </button>
                </div>
              }
            </div>
          }
        </div>
        @if (reports.length) {
          <div class="toolbar">
            <mat-form-field appearance="outline">
              <mat-label>Search reports</mat-label>
              <input matInput [(ngModel)]="searchValue" placeholder="Name, scope, format, delivery" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Status</mat-label>
              <mat-select [(ngModel)]="statusFilter">
                <mat-option value="all">All statuses</mat-option>
                <mat-option value="draft">Draft</mat-option>
                <mat-option value="active">Active</mat-option>
                <mat-option value="paused">Paused</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Scope</mat-label>
              <mat-select [(ngModel)]="scopeFilter">
                <mat-option value="all">All scopes</mat-option>
                <mat-option value="partner">Partner</mat-option>
                <mat-option value="company">Company</mat-option>
                <mat-option value="team">Team</mat-option>
                <mat-option value="global">Global</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Delivery</mat-label>
              <mat-select [(ngModel)]="deliveryFilter">
                <mat-option value="all">All delivery types</mat-option>
                <mat-option value="ftp">SFTP</mat-option>
                <mat-option value="email">Email</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
          @if (!isListLoading) {
            <div class="table-shell">
              @if (filteredReports.length) {
                <table
                  class="reports-table desktop-table"
                  matSort
                  [matSortActive]="reportListSort.active"
                  [matSortDirection]="reportListSort.direction"
                  [matSortDisableClear]="false"
                  (matSortChange)="onReportListSortChange($event)"
                  >
                  <thead>
                    <tr>
                      <th mat-sort-header="name">Name</th>
                      <th mat-sort-header="reportType">Tab type</th>
                      <th mat-sort-header="scope">Scope</th>
                      <th mat-sort-header="outputFormat">Format</th>
                      <th mat-sort-header="deliveryChannel">Delivery</th>
                      <th mat-sort-header="schedule">Schedule summary</th>
                      <th mat-sort-header="status">Status</th>
                      <th mat-sort-header="lastRunAt">Last run</th>
                      <th mat-sort-header="lastResult">Last result</th>
                      <th mat-sort-header="nextRunAt">Next run</th>
                      <th class="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (report of filteredReports; track trackById($index, report)) {
                      <tr>
                        <td>
                          <div class="primary-cell">{{ report.name }}</div>
                          @if (report.description) {
                            <div class="secondary-cell">{{ report.description }}</div>
                          }
                        </td>
                        <td>{{ getReportTypeLabel(report.reportType) }}</td>
                        <td>{{ getScopeLabel(report) }}</td>
                        <td>{{ getFormatLabel(report.outputFormat) }}</td>
                        <td>{{ getDeliveryLabel(report.deliveryChannel) }}</td>
                        <td>{{ getScheduleSummary(report) }}</td>
                        <td>
                          <span class="status-pill" [class.active]="report.status === 'active'" [class.paused]="report.status === 'paused'">
                            {{ report.status }}
                          </span>
                        </td>
                        <td>{{ formatReportDateTime(report.lastRunAt, report.timezone) }}</td>
                        <td>{{ getLastResultLabel(report) }}</td>
                        <td>{{ formatReportDateTime(report.nextRunAt, report.timezone) }}</td>
                        <td class="actions-col">
                          <button
                            mat-icon-button
                            type="button"
                            [matMenuTriggerFor]="reportActionsMenu"
                            [matMenuTriggerData]="{ report: report }"
                            aria-label="Report actions"
                            >
                            <mat-icon>more_vert</mat-icon>
                          </button>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              } @else {
                <section class="empty-inline-state">
                  <h3>No reports match the current filters.</h3>
                  <p>Adjust the search text or filters to see more scheduled reports.</p>
                </section>
              }
              @if (filteredReports.length) {
                <div class="mobile-report-list">
                  @for (report of filteredReports; track trackById($index, report)) {
                    <article class="mobile-report-card">
                      <div class="mobile-report-head">
                        <div>
                          <h3>{{ report.name }}</h3>
                          <p>{{ getReportTypeLabel(report.reportType) }}</p>
                        </div>
                        <span class="status-pill" [class.active]="report.status === 'active'" [class.paused]="report.status === 'paused'">
                          {{ report.status }}
                        </span>
                      </div>
                      <div class="mobile-report-grid">
                        <div>
                          <span class="mobile-label">Scope</span>
                          <span>{{ getScopeLabel(report) }}</span>
                        </div>
                        <div>
                          <span class="mobile-label">Format</span>
                          <span>{{ getFormatLabel(report.outputFormat) }}</span>
                        </div>
                        <div>
                          <span class="mobile-label">Delivery</span>
                          <span>{{ getDeliveryLabel(report.deliveryChannel) }}</span>
                        </div>
                        <div>
                          <span class="mobile-label">Next run</span>
                          <span>{{ formatReportDateTime(report.nextRunAt, report.timezone) }}</span>
                        </div>
                      </div>
                      <p class="mobile-schedule">{{ getScheduleSummary(report) }}</p>
                      <div class="mobile-actions">
                        <button
                          mat-stroked-button
                          type="button"
                          [matMenuTriggerFor]="reportActionsMenu"
                          [matMenuTriggerData]="{ report: report }"
                          >
                          More
                        </button>
                      </div>
                    </article>
                  }
                </div>
              }
            </div>
          } @else {
            <section class="inline-loading">
              <mat-spinner [diameter]="28"></mat-spinner>
              <span>Loading reports</span>
            </section>
          }
        } @else {
          <section class="empty-state">
            <mat-icon>description</mat-icon>
            <h2>No reports yet</h2>
          </section>
        }
        @if (canViewGroupProductOnboardingReport) {
          <section
            class="group-product-report"
            >
            <mat-expansion-panel class="group-product-report__panel" [expanded]="false">
              <mat-expansion-panel-header>
                <mat-panel-title>
                  <div class="group-product-report__title-row">
                    <span>Product Approval Status</span>
                    <span class="group-product-report__meta">{{ groupProductOnboardingReportVisibleRowCountLabel }}</span>
                  </div>
                </mat-panel-title>
                <mat-panel-description>
                  <div class="group-product-report__summary-pills">
                    <button
                      type="button"
                      class="group-product-report__summary-pill all"
                      [class.active]="isGroupProductOnboardingStatusFilterSelected('all')"
                      (click)="setGroupProductOnboardingStatusFilter('all', $event)"
                      >
                      All {{ groupProductOnboardingReportRows.length }}
                    </button>
                    <button
                      type="button"
                      class="group-product-report__summary-pill pending"
                      [class.active]="isGroupProductOnboardingStatusFilterSelected('pending')"
                      (click)="setGroupProductOnboardingStatusFilter('pending', $event)"
                      >
                      Pending {{ groupProductOnboardingReportPendingCount }}
                    </button>
                    <button
                      type="button"
                      class="group-product-report__summary-pill ready"
                      [class.active]="isGroupProductOnboardingStatusFilterSelected('ready')"
                      (click)="setGroupProductOnboardingStatusFilter('ready', $event)"
                      >
                      Ready {{ groupProductOnboardingReportReadyCount }}
                    </button>
                  </div>
                </mat-panel-description>
              </mat-expansion-panel-header>
              @if (isGroupProductOnboardingReportLoading) {
                <section class="inline-loading group-product-report__loading">
                  <mat-spinner [diameter]="24"></mat-spinner>
                  <span>Loading product approval status</span>
                </section>
              }
              @if (!isGroupProductOnboardingReportLoading) {
                <div class="group-product-report__body-header">
                  <p class="group-product-report__body-copy">
                    Internal overview of companies with approval-required payroll products and their readiness state.
                  </p>
                  <button
                    mat-stroked-button
                    type="button"
                    [disabled]="!filteredGroupProductOnboardingReportRows.length"
                    (click)="exportGroupProductOnboardingReport()"
                    >
                    Export CSV
                  </button>
                </div>
                @if (!groupProductOnboardingReportRows.length) {
                  <div class="group-product-report__empty">
                    No companies with approval-required payroll products are showing status rows yet.
                  </div>
                }
                @if (groupProductOnboardingReportRows.length && !filteredGroupProductOnboardingReportRows.length) {
                  <div
                    class="group-product-report__empty"
                    >
                    No rows match the selected status filter.
                  </div>
                }
                @if (filteredGroupProductOnboardingReportRows.length) {
                  <div
                    class="table-shell group-product-report__table-shell"
                    >
                    <table class="reports-table reports-table--compact">
                      <thead>
                        <tr>
                          <th>Company</th>
                          <th>Provider</th>
                          <th>Plan</th>
                          <th>Status</th>
                          <th>Requested By</th>
                          <th>Contact</th>
                          <th>Requested At</th>
                          <th>Ready By</th>
                          <th>Ready At</th>
                          <th class="actions-col">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        @for (row of filteredGroupProductOnboardingReportRows; track trackGroupProductOnboardingRow($index, row)) {
                          <tr>
                            <td>{{ row.companyName || '—' }}</td>
                            <td>{{ row.providerName || '—' }}</td>
                            <td>{{ row.planName || '—' }}</td>
                            <td>
                              <span
                                class="status-pill"
                                [class.active]="row.status === 'ready'"
                                [class.paused]="row.status === 'pending'"
                                >
                                {{ getGroupProductOnboardingStatusLabel(row) }}
                              </span>
                            </td>
                            <td>{{ row.userName || row.requestedBy.name || '—' }}</td>
                            <td>
                              <div>{{ row.userEmail || row.requestedBy.email || '—' }}</div>
                              @if (row.userPhone || row.requestedBy.phone) {
                                <div class="secondary-cell">
                                  {{ row.userPhone || row.requestedBy.phone }}
                                </div>
                              }
                            </td>
                            <td>{{ formatReportDateTime(row.requestedAt, 'America/Denver') }}</td>
                            <td>{{ row.readyBy.name || '—' }}</td>
                            <td>{{ row.readyAt ? formatReportDateTime(row.readyAt, 'America/Denver') : '—' }}</td>
                            <td class="actions-col group-product-report__actions-col">
                              @if (canMarkGroupProductOnboardingReady(row)) {
                                <button
                                  mat-stroked-button
                                  type="button"
                                  [disabled]="isMarkingGroupProductOnboardingReady(row) || isDeletingGroupProductOnboardingRequest(row)"
                                  (click)="markGroupProductOnboardingReadyFromReport(row)"
                                  >
                                  {{ isMarkingGroupProductOnboardingReady(row) ? 'Marking...' : 'Mark Ready' }}
                                </button>
                              }
                              @if (canDeleteGroupProductOnboardingRequest(row)) {
                                <button
                                  mat-button
                                  color="warn"
                                  type="button"
                                  [disabled]="isMarkingGroupProductOnboardingReady(row) || isDeletingGroupProductOnboardingRequest(row)"
                                  (click)="deleteGroupProductOnboardingRequestFromReport(row)"
                                  >
                                  {{ isDeletingGroupProductOnboardingRequest(row) ? 'Deleting...' : 'Delete' }}
                                </button>
                              }
                              @if (!canMarkGroupProductOnboardingReady(row) && !canDeleteGroupProductOnboardingRequest(row)) {
                                <span>—</span>
                              }
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                }
              }
            </mat-expansion-panel>
          </section>
        }
      </section>
    }
    @if (viewMode === 'create' || viewMode === 'edit') {
      <section class="editor-shell">
        <div class="editor-topline">
          <div>
            <h2>{{ viewMode === 'create' ? 'Create scheduled report' : 'Edit scheduled report' }}</h2>
            <p>
              Build the report basics, definition, delivery, schedule, and preview before activation.
            </p>
          </div>
          <div class="editor-type-summary">
            <span class="editor-type-summary-label">Report type</span>
            <strong>{{ getReportTypeLabel($safeNavigationMigration(reportForm.get('reportType')?.value)) }}</strong>
          </div>
        </div>
        <form [formGroup]="reportForm" class="editor-grid">
          <article class="panel">
            <div class="panel-header">
              <span class="panel-step">1</span>
              <div>
                <h3>Report basics</h3>
                <p>Name the report, add a description, and set the scope.</p>
              </div>
            </div>
            <div class="form-grid two-up">
              <mat-form-field appearance="outline">
                <mat-label>Report name</mat-label>
                <input matInput formControlName="name" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Status</mat-label>
                <mat-select formControlName="status">
                  <mat-option value="draft">Draft</mat-option>
                  <mat-option value="active">Active</mat-option>
                  <mat-option value="paused">Paused</mat-option>
                </mat-select>
              </mat-form-field>
              <div class="toggle-row">
                <mat-slide-toggle formControlName="showReportDates">Show report dates</mat-slide-toggle>
              </div>
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Description</mat-label>
                <textarea matInput rows="3" formControlName="description"></textarea>
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Scope type</mat-label>
                <mat-select formControlName="scopeType">
                  @for (scopeType of scopeTypes; track scopeType) {
                    <mat-option [value]="scopeType.value">
                      {{ scopeType.label }}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
              @if (reportForm.get('scopeType')?.value !== 'global') {
                <mat-form-field appearance="outline">
                  <mat-label>{{ getScopeSelectorLabel(reportForm.get('scopeType')?.value || '') }}</mat-label>
                  <mat-select multiple formControlName="scopeIds">
                    @if (shouldShowSelectSearch(getScopeOptions(reportForm.get('scopeType')?.value || '').length)) {
                      <mat-option>
                        <ngx-mat-select-search [formControl]="scopeSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                      </mat-option>
                    }
                    @for (scope of getFilteredScopeOptions(reportForm.get('scopeType')?.value || ''); track scope) {
                      <mat-option [value]="scope.id">
                        {{ scope.label }}@if (scope.secondaryLabel) {
                        <span> - {{ scope.secondaryLabel }}</span>
                      }
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }
            @if (reportForm.get('scopeType')?.value !== 'global' && !getScopeOptions(reportForm.get('scopeType')?.value || '').length) {
              <div class="info-card">
                @if (reportForm.get('scopeType')?.value === 'team' && areTeamOptionsLoading) {
                  <strong>
                    Loading team options
                  </strong>
                } @else {
                  <strong>No {{ reportForm.get('scopeType')?.value }} options available</strong>
                }
                @if (reportForm.get('scopeType')?.value === 'team' && areTeamOptionsLoading) {
                  <p>
                    Team options are loading for the current scope.
                  </p>
                }
                @if (reportForm.get('scopeType')?.value !== 'team' || !areTeamOptionsLoading) {
                  <p>
                    There are no selectable {{ reportForm.get('scopeType')?.value }} records for the current user.
                  </p>
                }
              </div>
            }
          </div>
        </article>
        <article class="panel">
          <div class="panel-header">
            <span class="panel-step">2</span>
            <div>
              <h3>Data definition</h3>
              @if (isTemplateReport) {
                <p>
                  Choose the canned report template and fill in the supported controls.
                </p>
              } @else {
                <p>Choose approved fields, filters, and sort rules for the flat-file report.</p>
              }
            </div>
          </div>
          @if (isTemplateReport) {
            <div class="form-grid two-up">
              <mat-form-field appearance="outline">
                <mat-label>Report template</mat-label>
                <mat-select formControlName="templateKey">
                  @if (shouldShowSelectSearch(templates.length)) {
                    <mat-option>
                      <ngx-mat-select-search [formControl]="templateSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                    </mat-option>
                  }
                  @for (template of getFilteredTemplates(); track trackById($index, template)) {
                    <mat-option [value]="template.key">
                      {{ template.label }}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
              @if (selectedTemplate) {
                <div class="info-card">
                  <strong>{{ selectedTemplate.label }}</strong>
                  <p>{{ selectedTemplate.description }}</p>
                </div>
              }
              <ng-container [formGroup]="templateArgsGroup">
                @if (selectedTemplateNeedsVendorSelection) {
                  <mat-form-field appearance="outline">
                    <mat-label>Vendors</mat-label>
                    <mat-select
                      multiple
                      formControlName="vendorSlugs"
                      (selectionChange)="onTemplateVendorSelectionChange()"
                      >
                      @if (shouldShowSelectSearch(vendorOptions.length)) {
                        <mat-option>
                          <ngx-mat-select-search [formControl]="templateVendorSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                        </mat-option>
                      }
                      <mat-option [value]="'__all_vendors__'" (onSelectionChange)="onAllTemplateVendorsToggle($event)">All vendors</mat-option>
                      @for (vendor of getFilteredVendorOptions(); track trackById($index, vendor)) {
                        <mat-option [value]="vendor.value">
                          {{ vendor.label }}
                        </mat-option>
                      }
                    </mat-select>
                    @if (allTemplateVendorOptionsSelected) {
                      <mat-hint>All vendors are included by default.</mat-hint>
                    }
                  </mat-form-field>
                }
                @if (selectedTemplateNeedsProductSelection) {
                  <mat-form-field appearance="outline">
                    <mat-label>Products</mat-label>
                    <mat-select multiple formControlName="productSlugs">
                      @if (shouldShowSelectSearch(getAvailableTemplateProducts().length)) {
                        <mat-option>
                          <ngx-mat-select-search [formControl]="templateProductSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                        </mat-option>
                      }
                      @for (product of getFilteredTemplateProducts(); track trackById($index, product)) {
                        <mat-option
                          [value]="getTemplateProductOptionValue(product)"
                          >
                          {{ product.vendorName || product.vendorSlug }} / {{ product.productName || product.productSlug }}
                        </mat-option>
                      }
                    </mat-select>
                    <mat-hint>Leave blank to include all products from the selected vendors.</mat-hint>
                  </mat-form-field>
                }
                @if (selectedTemplateSupportsEnrollmentStatusFilter) {
                  <div class="form-grid two-up full-width">
                    <mat-form-field appearance="outline">
                      <mat-label>Enrollment status</mat-label>
                      <mat-select formControlName="enrollmentStatusFilter">
                        @for (option of templateEnrollmentStatusFilters; track option) {
                          <mat-option [value]="option.value">
                            {{ option.label }}
                          </mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                    <div class="info-card">
                      <strong>Roster filter</strong>
                      <p>Choose whether to include all rows, only enrolled rows, or only rows currently marked `never_enrolled`.</p>
                    </div>
                  </div>
                }
                <div class="toggle-row full-width">
                  <mat-slide-toggle formControlName="includeUsersWithoutCurrentTeamMembership">
                    Include users without current team membership
                  </mat-slide-toggle>
                  <p>Include scoped users even if they do not currently have an active team membership row.</p>
                </div>
              </ng-container>
              @if (selectedTemplateSupportsColumnCustomization) {
                <section class="builder-panel full-width">
                  <div class="builder-header">
                    <h4>Output columns</h4>
                    <p>Drag to reorder the export columns and edit the final header text inline.</p>
                  </div>
                  <div cdkDropList class="selected-list" (cdkDropListDropped)="onTemplateColumnDrop($event)">
                    @for (column of templateColumnsArray.controls; track column; let i = $index) {
                      <div
                        class="selected-card"
                        [formGroup]="asFormGroup(column)"
                        cdkDrag
                        >
                        <div class="selected-card-head">
                          <span class="drag-handle" cdkDragHandle>drag_indicator</span>
                          <div class="selected-card-copy">
                            <strong>{{ getDisplayColumnHeader(column.get('header')?.value || column.get('defaultHeader')?.value || '') }}</strong>
                            <p>{{ getTemplateColumnFieldLabel(column.get('field')?.value || '') }}</p>
                          </div>
                        </div>
                        <div class="form-grid two-up compact">
                          <mat-form-field appearance="outline">
                            <mat-label>Column label</mat-label>
                            <input matInput formControlName="header" [placeholder]="column.get('defaultHeader')?.value || ''" />
                          </mat-form-field>
                          <div class="template-column-meta">
                            <span class="meta-label">Source field</span>
                            <strong>{{ column.get('field')?.value }}</strong>
                          </div>
                          <div class="arg-toggle">
                            <mat-checkbox formControlName="reportTotal" [disabled]="!isReportTotalField(column.get('field')?.value || '')">
                              Report total
                            </mat-checkbox>
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                </section>
              }
              @if (selectedTemplate?.key === 'partner_subscription_roster') {
                <div class="notice-card warning">
                  <h4>Subscription Status</h4>
                  <p>Derived from product subscription history. This does not reflect user account status.</p>
                </div>
              }
              @if (selectedTemplate?.key === 'survey_answers_by_user') {
                <div class="notice-card">
                  <h4>One row per survey submission</h4>
                  <p>Answers uses survey `formData` only.</p>
                </div>
              }
              @if (selectedTemplate?.key === 'survey_answers_by_question') {
                <div class="notice-card">
                  <h4>One row per extracted answer</h4>
                  <p>Question-level rows are flattened from survey response JSON when possible. Preview fallback notes should be treated as informational.</p>
                </div>
              }
            </div>
          } @else {
            <div class="dataset-grid">
              @for (dataset of datasetOptions; track trackById($index, dataset)) {
                <button
                  type="button"
                  class="dataset-card"
                  [class.active]="reportForm.get('baseEntity')?.value === dataset.key"
                  [class.disabled]="!dataset.enabled"
                  [disabled]="!dataset.enabled"
                  (click)="reportForm.get('baseEntity')?.setValue(dataset.key)"
                  >
                  <strong>{{ dataset.label }}</strong>
                  <span>{{ dataset.note }}</span>
                </button>
              }
            </div>
            <div class="toggle-row" [formGroup]="customAudienceGroup">
              <mat-slide-toggle formControlName="includeUsersWithoutCurrentTeamMembership">
                Include users without current team membership
              </mat-slide-toggle>
              <p>Include scoped users even if they do not currently have an active team membership row.</p>
            </div>
            <div class="subsection">
              <div class="builder-header">
                <h4>Output shape</h4>
                <p>Choose between row-level exports and grouped count reports.</p>
              </div>
              <mat-button-toggle-group formControlName="resultMode" aria-label="Custom report output shape">
                @for (mode of customReportResultModes; track mode) {
                  <mat-button-toggle [value]="mode.value">
                    {{ mode.label }}
                  </mat-button-toggle>
                }
              </mat-button-toggle-group>
              <div class="info-card">
                <strong>{{ isAggregatedCustomReport ? 'Grouped counts' : 'Detail rows' }}</strong>
                <p>{{ (isAggregatedCustomReport ? customReportResultModes[1] : customReportResultModes[0]).description }}</p>
              </div>
            </div>
            <div class="builder-grid">
              <section class="builder-panel">
                <div class="builder-header">
                  <h4>Field catalog</h4>
                  <p>Grouped by business area, not by raw SQL join name.</p>
                </div>
                @if (!groupedFields.length) {
                  <div class="info-card">
                    <strong>No approved fields available yet</strong>
                    <p>The selected dataset is ready in the UI, but the backend field catalog has not advertised fields for it yet.</p>
                  </div>
                }
                @for (group of groupedFields; track trackByGroupName($index, group)) {
                  <div class="field-group">
                    <h5>{{ group.group }}</h5>
                    @for (field of group.fields; track trackById($index, field)) {
                      <div class="field-row">
                        <div class="field-copy">
                          <strong>{{ field.label }}</strong>
                          <p>{{ field.description }}</p>
                        </div>
                        <div class="field-meta">
                          <span class="type-badge">{{ field.type }}</span>
                          <button mat-stroked-button type="button" (click)="isAggregatedCustomReport ? addGroupBy(field) : addColumn(field)">
                            {{ isAggregatedCustomReport ? 'Add level' : 'Add column' }}
                          </button>
                        </div>
                      </div>
                    }
                  </div>
                }
              </section>
              <section class="builder-panel">
                @if (!isAggregatedCustomReport) {
                  <div class="builder-header">
                    <h4>Output columns</h4>
                    <p>Drag to reorder the final column sequence and choose field, template, or static-value output.</p>
                  </div>
                  @if (!columnsArray.length) {
                    <div class="empty-inline-state">
                      Choose at least one output column.
                    </div>
                  }
                  <div cdkDropList class="selected-list" (cdkDropListDropped)="onColumnDrop($event)">
                    @for (column of columnsArray.controls; track column; let i = $index) {
                      <div class="selected-card" [formGroup]="asFormGroup(column)" cdkDrag>
                        <div class="selected-card-head">
                          <span class="drag-handle" cdkDragHandle>drag_indicator</span>
                          <div class="selected-card-copy">
                            <strong>{{ column.get('header')?.value || ('Column ' + (i + 1)) }}</strong>
                            <p>{{ $safeNavigationMigration(column.get('kind')?.value) | titlecase }} column</p>
                          </div>
                          <button mat-icon-button type="button" (click)="removeColumn(i)">
                            <mat-icon>close</mat-icon>
                          </button>
                        </div>
                        <div class="form-grid three-up compact">
                          <mat-form-field appearance="outline">
                            <mat-label>Column type</mat-label>
                            <mat-select formControlName="kind" (selectionChange)="onColumnKindChange(column)">
                              @for (kind of reportColumnKinds; track kind) {
                                <mat-option [value]="kind.value">
                                  {{ kind.label }}
                                </mat-option>
                              }
                            </mat-select>
                          </mat-form-field>
                          <mat-form-field appearance="outline">
                            <mat-label>Column label</mat-label>
                            <input matInput formControlName="header" />
                          </mat-form-field>
                          <div class="arg-toggle">
                            <mat-checkbox formControlName="reportTotal" [disabled]="!canReportTotalColumn(column)">
                              Report total
                            </mat-checkbox>
                          </div>
                          @if (column.get('kind')?.value === 'field') {
                            <mat-form-field appearance="outline">
                              <mat-label>Transform</mat-label>
                              <mat-select formControlName="transform">
                                @for (transform of outputTransforms; track transform) {
                                  <mat-option [value]="transform.value">
                                    {{ transform.label }}
                                  </mat-option>
                                }
                              </mat-select>
                            </mat-form-field>
                          }
                          @if (column.get('kind')?.value === 'field') {
                            <mat-form-field appearance="outline">
                              <mat-label>Source field</mat-label>
                              <mat-select formControlName="field" (selectionChange)="onColumnFieldChange(column)">
                                @if (shouldShowSelectSearch(getGroupedFieldCount())) {
                                  <mat-option>
                                    <ngx-mat-select-search [formControl]="fieldSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                  </mat-option>
                                }
                                @for (group of getFilteredGroupedFields(); track trackByGroupName($index, group)) {
                                  @for (field of group.fields; track trackById($index, field)) {
                                    <mat-option [value]="field.key">
                                      {{ field.label }}
                                    </mat-option>
                                  }
                                }
                              </mat-select>
                            </mat-form-field>
                          }
                          @if (isDateFormatColumn(column)) {
                            <mat-form-field appearance="outline">
                              <mat-label>Date Format</mat-label>
                              <mat-select formControlName="dateFormatMode">
                                @for (option of dateFormatOptions; track option) {
                                  <mat-option [value]="option.value">
                                    {{ option.label }}
                                  </mat-option>
                                }
                              </mat-select>
                            </mat-form-field>
                          }
                          @if (isCustomDateFormatColumn(column)) {
                            <mat-form-field appearance="outline">
                              <mat-label>Custom Date Format</mat-label>
                              <input matInput formControlName="customDateFormat" />
                            </mat-form-field>
                          }
                          @if (column.get('kind')?.value === 'field' && fieldRequiresArgs(column.get('field')?.value || '')) {
                            <ng-container [formGroup]="getArgsGroup(column)">
                              @for (arg of getFieldArgEntries(column.get('field')?.value || ''); track trackByArgKey($index, arg)) {
                                @if (isVendorSelectionArg(arg.key, arg.schema)) {
                                  <mat-form-field appearance="outline">
                                    <mat-label>Vendors</mat-label>
                                    <mat-select multiple formControlName="vendorSlugs" (selectionChange)="onVendorSelectionChange(column)">
                                      @if (shouldShowSelectSearch(vendorOptions.length)) {
                                        <mat-option>
                                          <ngx-mat-select-search [formControl]="semanticVendorSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                        </mat-option>
                                      }
                                      @for (vendor of getFilteredVendorOptions(semanticVendorSearchControl); track trackById($index, vendor)) {
                                        <mat-option [value]="vendor.value">
                                          {{ vendor.label }}
                                        </mat-option>
                                      }
                                    </mat-select>
                                  </mat-form-field>
                                  <mat-form-field appearance="outline">
                                    <mat-label>Products</mat-label>
                                    <mat-select multiple formControlName="productIds">
                                      @if (shouldShowSelectSearch(getAvailableVendorSelectionProducts(column).length)) {
                                        <mat-option>
                                          <ngx-mat-select-search [formControl]="semanticProductSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                        </mat-option>
                                      }
                                      @for (product of getFilteredVendorSelectionProducts(column); track trackById($index, product)) {
                                        <mat-option
                                          [value]="product.id"
                                          >
                                          {{ product.label }}
                                        </mat-option>
                                      }
                                    </mat-select>
                                  </mat-form-field>
                                } @else {
                                  @if (arg.schema.type === 'boolean') {
                                    <div class="arg-toggle">
                                      <mat-slide-toggle [formControlName]="arg.key">{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-slide-toggle>
                                    </div>
                                  } @else {
                                    <mat-form-field appearance="outline">
                                      <mat-label>{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-label>
                                      <input matInput [formControlName]="arg.key" />
                                    </mat-form-field>
                                  }
                                }
                              }
                            </ng-container>
                          }
                          @if (column.get('kind')?.value === 'template') {
                            <mat-form-field appearance="outline" class="full-width">
                              <mat-label>Template</mat-label>
                              <textarea matInput rows="3" formControlName="template"></textarea>
                            </mat-form-field>
                            <div class="info-card full-width">
                              <strong>Runtime tokens are available inside templates</strong>
                              <p>
                                Use <code ngNonBindable>{{windowStart}}</code>, <code ngNonBindable>{{windowEnd}}</code>, or
                                <code ngNonBindable>{{asOfAt}}</code>, and add a format like
                                <code ngNonBindable>{{windowStart:YYYYMMDD}}</code> when needed.
                              </p>
                            </div>
                            <div class="full-width template-source-list">
                              @for (source of getColumnSourcesArray(column).controls; track source; let sourceIndex = $index) {
                                <div class="template-source-card" [formGroup]="asFormGroup(source)">
                                  <div class="selected-card-head nested">
                                    <strong>Source {{ sourceIndex + 1 }}</strong>
                                    <button mat-icon-button type="button" (click)="removeTemplateSource(column, sourceIndex)">
                                      <mat-icon>close</mat-icon>
                                    </button>
                                  </div>
                                  <div class="form-grid three-up compact">
                                    <mat-form-field appearance="outline">
                                      <mat-label>Token</mat-label>
                                      <input matInput formControlName="token" />
                                    </mat-form-field>
                                    <mat-form-field appearance="outline">
                                      <mat-label>Field</mat-label>
                                      <mat-select formControlName="field" (selectionChange)="onTemplateSourceFieldChange(source)">
                                        @if (shouldShowSelectSearch(getGroupedFieldCount())) {
                                          <mat-option>
                                            <ngx-mat-select-search [formControl]="fieldSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                          </mat-option>
                                        }
                                        @for (group of getFilteredGroupedFields(); track trackByGroupName($index, group)) {
                                          @for (field of group.fields; track trackById($index, field)) {
                                            <mat-option [value]="field.key">
                                              {{ field.label }}
                                            </mat-option>
                                          }
                                        }
                                      </mat-select>
                                    </mat-form-field>
                                    <mat-form-field appearance="outline">
                                      <mat-label>Transform</mat-label>
                                      <mat-select formControlName="transform">
                                        @for (transform of outputTransforms; track transform) {
                                          <mat-option [value]="transform.value">
                                            {{ transform.label }}
                                          </mat-option>
                                        }
                                      </mat-select>
                                    </mat-form-field>
                                    @if (fieldRequiresArgs(source.get('field')?.value || '')) {
                                      <ng-container [formGroup]="getArgsGroup(source)">
                                        @for (arg of getFieldArgEntries(source.get('field')?.value || ''); track trackByArgKey($index, arg)) {
                                          @if (isVendorSelectionArg(arg.key, arg.schema)) {
                                            <mat-form-field appearance="outline">
                                              <mat-label>Vendors</mat-label>
                                              <mat-select multiple formControlName="vendorSlugs" (selectionChange)="onVendorSelectionChange(source)">
                                                @if (shouldShowSelectSearch(vendorOptions.length)) {
                                                  <mat-option>
                                                    <ngx-mat-select-search [formControl]="semanticVendorSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                                  </mat-option>
                                                }
                                                @for (vendor of getFilteredVendorOptions(semanticVendorSearchControl); track trackById($index, vendor)) {
                                                  <mat-option [value]="vendor.value">
                                                    {{ vendor.label }}
                                                  </mat-option>
                                                }
                                              </mat-select>
                                            </mat-form-field>
                                            <mat-form-field appearance="outline">
                                              <mat-label>Products</mat-label>
                                              <mat-select multiple formControlName="productIds">
                                                @if (shouldShowSelectSearch(getAvailableVendorSelectionProducts(source).length)) {
                                                  <mat-option>
                                                    <ngx-mat-select-search [formControl]="semanticProductSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                                  </mat-option>
                                                }
                                                @for (product of getFilteredVendorSelectionProducts(source); track trackById($index, product)) {
                                                  <mat-option
                                                    [value]="product.id"
                                                    >
                                                    {{ product.label }}
                                                  </mat-option>
                                                }
                                              </mat-select>
                                            </mat-form-field>
                                          } @else {
                                            @if (arg.schema.type === 'boolean') {
                                              <div class="arg-toggle">
                                                <mat-slide-toggle [formControlName]="arg.key">{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-slide-toggle>
                                              </div>
                                            } @else {
                                              <mat-form-field appearance="outline">
                                                <mat-label>{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-label>
                                                <input matInput [formControlName]="arg.key" />
                                              </mat-form-field>
                                            }
                                          }
                                        }
                                      </ng-container>
                                    }
                                  </div>
                                </div>
                              }
                            </div>
                            <div class="builder-footer full-width">
                              <button mat-stroked-button type="button" (click)="addTemplateSource(column)">Add template source</button>
                            </div>
                          }
                          @if (column.get('kind')?.value === 'value') {
                            <mat-form-field appearance="outline" class="full-width">
                              <mat-label>Static value</mat-label>
                              <input matInput formControlName="value" />
                            </mat-form-field>
                          }
                        </div>
                      </div>
                    }
                  </div>
                  <div class="builder-footer">
                    <button mat-stroked-button type="button" (click)="addColumn()">Add another column</button>
                  </div>
                } @else {
                  <div class="builder-header">
                    <h4>Group-by levels</h4>
                    <p>Each level becomes an output column. The final count column is added automatically.</p>
                  </div>
                  @if (!groupByArray.length) {
                    <div class="empty-inline-state">
                      Choose at least one group-by level.
                    </div>
                  }
                  <div class="selected-list">
                    @for (groupBy of groupByArray.controls; track groupBy; let i = $index) {
                      <div class="selected-card" [formGroup]="asFormGroup(groupBy)">
                        <div class="selected-card-head">
                          <div class="selected-card-copy">
                            <strong>{{ groupBy.get('header')?.value || ('Level ' + (i + 1)) }}</strong>
                            <p>{{ getFieldLabel(groupBy.get('field')?.value || '') }}</p>
                          </div>
                          <button mat-icon-button type="button" (click)="removeGroupBy(i)">
                            <mat-icon>close</mat-icon>
                          </button>
                        </div>
                        <div class="form-grid three-up compact">
                          <mat-form-field appearance="outline">
                            <mat-label>Group field</mat-label>
                            <mat-select formControlName="field" (selectionChange)="onGroupByFieldChange(groupBy)">
                              @if (shouldShowSelectSearch(getGroupedFieldCount())) {
                                <mat-option>
                                  <ngx-mat-select-search [formControl]="fieldSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                </mat-option>
                              }
                              @for (group of getFilteredGroupedFields(); track trackByGroupName($index, group)) {
                                @for (field of group.fields; track trackById($index, field)) {
                                  <mat-option [value]="field.key">
                                    {{ field.label }}
                                  </mat-option>
                                }
                              }
                            </mat-select>
                          </mat-form-field>
                          <mat-form-field appearance="outline">
                            <mat-label>Column label</mat-label>
                            <input matInput formControlName="header" />
                          </mat-form-field>
                          @if (isDateFormatGroupBy(groupBy)) {
                            <mat-form-field appearance="outline">
                              <mat-label>Date Format</mat-label>
                              <mat-select formControlName="dateFormatMode">
                                @for (option of dateFormatOptions; track option) {
                                  <mat-option [value]="option.value">
                                    {{ option.label }}
                                  </mat-option>
                                }
                              </mat-select>
                            </mat-form-field>
                          }
                          @if (isCustomDateFormatGroupBy(groupBy)) {
                            <mat-form-field appearance="outline">
                              <mat-label>Custom Date Format</mat-label>
                              <input matInput formControlName="customDateFormat" />
                            </mat-form-field>
                          }
                          @if (fieldRequiresArgs(groupBy.get('field')?.value || '')) {
                            <ng-container [formGroup]="getArgsGroup(groupBy)">
                              @for (arg of getFieldArgEntries(groupBy.get('field')?.value || ''); track trackByArgKey($index, arg)) {
                                @if (isVendorSelectionArg(arg.key, arg.schema)) {
                                  <mat-form-field appearance="outline">
                                    <mat-label>Vendors</mat-label>
                                    <mat-select multiple formControlName="vendorSlugs" (selectionChange)="onVendorSelectionChange(groupBy)">
                                      @if (shouldShowSelectSearch(vendorOptions.length)) {
                                        <mat-option>
                                          <ngx-mat-select-search [formControl]="semanticVendorSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                        </mat-option>
                                      }
                                      @for (vendor of getFilteredVendorOptions(semanticVendorSearchControl); track trackById($index, vendor)) {
                                        <mat-option [value]="vendor.value">
                                          {{ vendor.label }}
                                        </mat-option>
                                      }
                                    </mat-select>
                                  </mat-form-field>
                                  <mat-form-field appearance="outline">
                                    <mat-label>Products</mat-label>
                                    <mat-select multiple formControlName="productIds">
                                      @if (shouldShowSelectSearch(getAvailableVendorSelectionProducts(groupBy).length)) {
                                        <mat-option>
                                          <ngx-mat-select-search [formControl]="semanticProductSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                        </mat-option>
                                      }
                                      @for (product of getFilteredVendorSelectionProducts(groupBy); track trackById($index, product)) {
                                        <mat-option
                                          [value]="product.id"
                                          >
                                          {{ product.label }}
                                        </mat-option>
                                      }
                                    </mat-select>
                                  </mat-form-field>
                                } @else {
                                  @if (arg.schema.type === 'boolean') {
                                    <div class="arg-toggle">
                                      <mat-slide-toggle [formControlName]="arg.key">{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-slide-toggle>
                                    </div>
                                  } @else {
                                    <mat-form-field appearance="outline">
                                      <mat-label>{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-label>
                                      <input matInput [formControlName]="arg.key" />
                                    </mat-form-field>
                                  }
                                }
                              }
                            </ng-container>
                          }
                        </div>
                      </div>
                    }
                  </div>
                  <div class="form-grid three-up compact" [formGroup]="aggregationGroup">
                    <mat-form-field appearance="outline">
                      <mat-label>Count distinct field</mat-label>
                      <mat-select formControlName="countDistinctField" (selectionChange)="onCountDistinctFieldChange()">
                        @if (shouldShowSelectSearch(getGroupedFieldCount())) {
                          <mat-option>
                            <ngx-mat-select-search [formControl]="fieldSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                          </mat-option>
                        }
                        @for (group of getFilteredGroupedFields(); track trackByGroupName($index, group)) {
                          @for (field of group.fields; track trackById($index, field)) {
                            <mat-option [value]="field.key">
                              {{ field.label }}
                            </mat-option>
                          }
                        }
                      </mat-select>
                    </mat-form-field>
                    @if (fieldRequiresArgs(aggregationGroup.get('countDistinctField')?.value || '')) {
                      <ng-container [formGroup]="aggregationCountDistinctArgsGroup">
                        @for (arg of getFieldArgEntries(aggregationGroup.get('countDistinctField')?.value || ''); track trackByArgKey($index, arg)) {
                          @if (arg.schema.type === 'boolean') {
                            <div class="arg-toggle">
                              <mat-slide-toggle [formControlName]="arg.key">{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-slide-toggle>
                            </div>
                          } @else {
                            <mat-form-field appearance="outline">
                              <mat-label>{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-label>
                              <input matInput [formControlName]="arg.key" />
                            </mat-form-field>
                          }
                        }
                      </ng-container>
                    }
                    <mat-form-field appearance="outline">
                      <mat-label>Count column label</mat-label>
                      <input matInput formControlName="countHeader" />
                    </mat-form-field>
                    <div class="arg-toggle">
                      <mat-checkbox formControlName="reportTotal">
                        Report total
                      </mat-checkbox>
                    </div>
                    <div class="info-card">
                      <strong>Applied after filters</strong>
                      <p>Filters run before aggregation. The count distinct field is counted once per grouped row.</p>
                    </div>
                  </div>
                  <div class="builder-footer">
                    <button mat-stroked-button type="button" (click)="addGroupBy()">Add group-by level</button>
                  </div>
                }
              </section>
            </div>
            <div class="subsection">
              <div class="builder-header">
                <h4>Filters</h4>
                <p>Use typed inputs instead of raw operators or JSON.</p>
              </div>
              @for (filter of filtersArray.controls; track filter; let i = $index) {
                <div class="selected-card" [formGroup]="asFormGroup(filter)">
                  <div class="selected-card-head">
                    <strong>Filter {{ i + 1 }}</strong>
                    <button mat-icon-button type="button" (click)="removeFilter(i)">
                      <mat-icon>close</mat-icon>
                    </button>
                  </div>
                  <div class="form-grid four-up compact">
                    <mat-form-field appearance="outline">
                      <mat-label>Field</mat-label>
                      <mat-select formControlName="field" (selectionChange)="onFilterFieldChange(filter)">
                        @if (shouldShowSelectSearch(getGroupedFieldCount())) {
                          <mat-option>
                            <ngx-mat-select-search [formControl]="fieldSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                          </mat-option>
                        }
                        @for (group of getFilteredGroupedFields(); track trackByGroupName($index, group)) {
                          @for (field of group.fields; track trackById($index, field)) {
                            <mat-option [value]="field.key">
                              {{ field.label }}
                            </mat-option>
                          }
                        }
                      </mat-select>
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Operator</mat-label>
                      <mat-select formControlName="operator">
                        @for (operator of getFieldOperators(filter.get('field')?.value || ''); track operator) {
                          <mat-option [value]="operator">
                            {{ getFieldOperatorLabel(operator) }}
                          </mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                    <div class="toggle-row compact">
                      <mat-slide-toggle [checked]="isRuntimeFilter(filter)" (change)="setRuntimeFilterMode(filter, $event.checked)">
                        Ask at run time
                      </mat-slide-toggle>
                    </div>
                    @if (!isRuntimeFilter(filter) && filterUsesSingleSelect(filter)) {
                      <mat-form-field appearance="outline">
                        <mat-label>Value</mat-label>
                        <mat-select formControlName="valueText">
                          @for (option of getFilterValueOptions(filter); track trackById($index, option)) {
                            <mat-option [value]="option.value">
                              {{ option.label }}
                            </mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                    }
                    @if (!isRuntimeFilter(filter) && filterUsesMultiSelect(filter)) {
                      <mat-form-field appearance="outline">
                        <mat-label>Values</mat-label>
                        <mat-select multiple formControlName="optionValues">
                          @for (option of getFilterValueOptions(filter); track trackById($index, option)) {
                            <mat-option [value]="option.value">
                              {{ option.label }}
                            </mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                    }
                    @if (!isRuntimeFilter(filter) && !filterUsesFiniteOptions(filter) && filterOperatorUsesValue($safeNavigationMigration(filter.get('operator')?.value)) && (getFieldType(filter.get('field')?.value || '') === 'string' || getFieldType(filter.get('field')?.value || '') === 'enum')) {
                      <mat-form-field appearance="outline">
                        <mat-label>{{ filterOperatorUsesMultipleValues($safeNavigationMigration(filter.get('operator')?.value)) ? 'Values' : 'Value' }}</mat-label>
                        @if (!filterOperatorUsesMultipleValues($safeNavigationMigration(filter.get('operator')?.value))) {
                          <input matInput formControlName="valueText" />
                        }
                        @if (filterOperatorUsesMultipleValues($safeNavigationMigration(filter.get('operator')?.value))) {
                          <textarea matInput rows="2" formControlName="multiValueText"></textarea>
                        }
                      </mat-form-field>
                    }
                    @if (!isRuntimeFilter(filter) && filterOperatorUsesValue($safeNavigationMigration(filter.get('operator')?.value)) && getFieldType(filter.get('field')?.value || '') === 'number') {
                      <mat-form-field appearance="outline">
                        <mat-label>Value</mat-label>
                        <input matInput type="number" formControlName="valueNumber" />
                      </mat-form-field>
                    }
                    @if (!isRuntimeFilter(filter) && filterOperatorUsesValue($safeNavigationMigration(filter.get('operator')?.value)) && getFieldType(filter.get('field')?.value || '') === 'boolean') {
                      <mat-form-field appearance="outline">
                        <mat-label>Value</mat-label>
                        <mat-select formControlName="valueBoolean">
                          <mat-option value="true">True</mat-option>
                          <mat-option value="false">False</mat-option>
                        </mat-select>
                      </mat-form-field>
                    }
                    @if (!isRuntimeFilter(filter) && filterOperatorUsesValue($safeNavigationMigration(filter.get('operator')?.value)) && getFieldType(filter.get('field')?.value || '') === 'date') {
                      <mat-form-field appearance="outline">
                        <mat-label>Value</mat-label>
                        <input matInput type="date" formControlName="valueDate" />
                      </mat-form-field>
                    }
                    @if (!isRuntimeFilter(filter) && filterOperatorUsesValue($safeNavigationMigration(filter.get('operator')?.value)) && getFieldType(filter.get('field')?.value || '') === 'datetime') {
                      <mat-form-field appearance="outline">
                        <mat-label>Value</mat-label>
                        <input matInput type="datetime-local" formControlName="valueDate" />
                      </mat-form-field>
                    }
                    @if (fieldRequiresArgs(filter.get('field')?.value || '')) {
                      <ng-container [formGroup]="getArgsGroup(filter)">
                        @for (arg of getFieldArgEntries(filter.get('field')?.value || ''); track trackByArgKey($index, arg)) {
                          @if (isVendorSelectionArg(arg.key, arg.schema)) {
                            <mat-form-field appearance="outline">
                              <mat-label>Vendors</mat-label>
                              <mat-select multiple formControlName="vendorSlugs" (selectionChange)="onVendorSelectionChange(filter)">
                                @if (shouldShowSelectSearch(vendorOptions.length)) {
                                  <mat-option>
                                    <ngx-mat-select-search [formControl]="semanticVendorSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                  </mat-option>
                                }
                                @for (vendor of getFilteredVendorOptions(semanticVendorSearchControl); track trackById($index, vendor)) {
                                  <mat-option [value]="vendor.value">
                                    {{ vendor.label }}
                                  </mat-option>
                                }
                              </mat-select>
                            </mat-form-field>
                            <mat-form-field appearance="outline">
                              <mat-label>Products</mat-label>
                              <mat-select multiple formControlName="productIds">
                                @if (shouldShowSelectSearch(getAvailableVendorSelectionProducts(filter).length)) {
                                  <mat-option>
                                    <ngx-mat-select-search [formControl]="semanticProductSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                  </mat-option>
                                }
                                @for (product of getFilteredVendorSelectionProducts(filter); track trackById($index, product)) {
                                  <mat-option
                                    [value]="product.id"
                                    >
                                    {{ product.label }}
                                  </mat-option>
                                }
                              </mat-select>
                            </mat-form-field>
                          } @else {
                            @if (arg.schema.type === 'boolean') {
                              <div class="arg-toggle">
                                <mat-slide-toggle [formControlName]="arg.key">{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-slide-toggle>
                              </div>
                            } @else {
                              <mat-form-field appearance="outline">
                                <mat-label>{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-label>
                                <input matInput [formControlName]="arg.key" />
                              </mat-form-field>
                            }
                          }
                        }
                      </ng-container>
                    }
                  </div>
                </div>
              }
              <button mat-stroked-button type="button" (click)="addFilter()">Add filter</button>
            </div>
          }
          @if (!isTemplateReport || selectedTemplateSupportsSortCustomization) {
            <div class="subsection">
              <div class="builder-header">
                <h4>Sort</h4>
                <p>Sort controls change the order of output rows in the final file.</p>
              </div>
              <div class="info-card">
                <strong>The first sort row has the highest priority.</strong>
                <p>Only sortable fields are available here. Drag rows to change the top-to-bottom precedence.</p>
              </div>
              @if (!sortArray.length) {
                <div class="empty-inline-state">
                  Add a sort row when the export needs a predictable reading order.
                </div>
              }
              <div cdkDropList class="selected-list" (cdkDropListDropped)="onSortDrop($event)">
                @for (sort of sortArray.controls; track sort; let i = $index) {
                  <div class="selected-card" [formGroup]="asFormGroup(sort)" cdkDrag>
                    <div class="selected-card-head">
                      <span class="drag-handle" cdkDragHandle>drag_indicator</span>
                      <div class="selected-card-copy">
                        <strong>{{ getFieldLabel(sort.get('field')?.value || '') }}</strong>
                        <p>Priority {{ i + 1 }} - {{ getSortDirectionLabel($safeNavigationMigration(sort.get('direction')?.value)) }}</p>
                      </div>
                      <button mat-icon-button type="button" (click)="removeSort(i)">
                        <mat-icon>delete</mat-icon>
                      </button>
                    </div>
                    <div class="form-grid two-up compact">
                      <mat-form-field appearance="outline">
                        <mat-label>Field</mat-label>
                        <mat-select formControlName="field" (selectionChange)="onSortFieldChange(sort)">
                          @if (shouldShowSelectSearch(getSortableFields().length)) {
                            <mat-option>
                              <ngx-mat-select-search [formControl]="sortFieldSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                            </mat-option>
                          }
                          @for (field of getFilteredSortableFields(); track trackById($index, field)) {
                            <mat-option [value]="field.key">
                              {{ field.label }}
                            </mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                      <mat-form-field appearance="outline">
                        <mat-label>Direction</mat-label>
                        <mat-select formControlName="direction">
                          <mat-option value="asc">Ascending</mat-option>
                          <mat-option value="desc">Descending</mat-option>
                        </mat-select>
                      </mat-form-field>
                      @if (fieldRequiresArgs(sort.get('field')?.value || '')) {
                        <ng-container [formGroup]="getArgsGroup(sort)">
                          @for (arg of getFieldArgEntries(sort.get('field')?.value || ''); track trackByArgKey($index, arg)) {
                            @if (isVendorSelectionArg(arg.key, arg.schema)) {
                              <mat-form-field appearance="outline">
                                <mat-label>Vendors</mat-label>
                                <mat-select multiple formControlName="vendorSlugs" (selectionChange)="onVendorSelectionChange(sort)">
                                  @if (shouldShowSelectSearch(vendorOptions.length)) {
                                    <mat-option>
                                      <ngx-mat-select-search [formControl]="semanticVendorSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                    </mat-option>
                                  }
                                  @for (vendor of getFilteredVendorOptions(semanticVendorSearchControl); track trackById($index, vendor)) {
                                    <mat-option [value]="vendor.value">
                                      {{ vendor.label }}
                                    </mat-option>
                                  }
                                </mat-select>
                              </mat-form-field>
                              <mat-form-field appearance="outline">
                                <mat-label>Products</mat-label>
                                <mat-select multiple formControlName="productIds">
                                  @if (shouldShowSelectSearch(getAvailableVendorSelectionProducts(sort).length)) {
                                    <mat-option>
                                      <ngx-mat-select-search [formControl]="semanticProductSearchControl" noEntriesFoundLabel="No matches" placeholderLabel="Search..."></ngx-mat-select-search>
                                    </mat-option>
                                  }
                                  @for (product of getFilteredVendorSelectionProducts(sort); track trackById($index, product)) {
                                    <mat-option
                                      [value]="product.id"
                                      >
                                      {{ product.label }}
                                    </mat-option>
                                  }
                                </mat-select>
                              </mat-form-field>
                            } @else {
                              @if (arg.schema.type === 'boolean') {
                                <div class="arg-toggle">
                                  <mat-slide-toggle [formControlName]="arg.key">{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-slide-toggle>
                                </div>
                              } @else {
                                <mat-form-field appearance="outline">
                                  <mat-label>{{ getFieldArgLabel(arg.key, arg.schema) }}</mat-label>
                                  <input matInput [formControlName]="arg.key" />
                                </mat-form-field>
                              }
                            }
                          }
                        </ng-container>
                      }
                    </div>
                  </div>
                }
              </div>
              <div class="builder-footer">
                <button mat-stroked-button type="button" (click)="addSort()">Add sort</button>
              </div>
            </div>
          }
        </article>
        <article class="panel">
          <div class="panel-header">
            <span class="panel-step">3</span>
            <div>
              <h3>Output</h3>
              <p>Select the file format and naming pattern.</p>
            </div>
          </div>
          <div class="form-grid two-up">
            <mat-form-field appearance="outline">
              <mat-label>Output format</mat-label>
              <mat-select formControlName="outputFormat">
                @for (format of outputFormats; track format) {
                  <mat-option [value]="format.value">{{ format.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>File name pattern</mat-label>
              <input matInput formControlName="fileNamePattern" />
            </mat-form-field>
          </div>
          <div class="info-card">
            <strong>File name preview</strong>
            <p>{{ fileNamePreview }}</p>
          </div>
        </article>
        <article class="panel">
          <div class="panel-header">
            <span class="panel-step">4</span>
            <div>
              <h3>Delivery</h3>
              <p>Select exactly one delivery channel.</p>
            </div>
          </div>
          <div class="form-grid two-up">
            <mat-form-field appearance="outline">
              <mat-label>Delivery channel</mat-label>
              <mat-select formControlName="deliveryChannel">
                @for (channel of deliveryChannels; track channel) {
                  <mat-option [value]="channel.value">{{ channel.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
          @if (reportForm.get('deliveryChannel')?.value === 'ftp') {
            <div class="form-grid two-up" [formGroup]="deliveryConfigGroup">
              <mat-form-field appearance="outline">
                <mat-label>Host</mat-label>
                <input matInput formControlName="host" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Port</mat-label>
                <input matInput type="number" formControlName="port" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Username</mat-label>
                <input matInput formControlName="username" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Folder</mat-label>
                <input matInput formControlName="folder" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Password secret reference</mat-label>
                <mat-select formControlName="passwordSecretKey">
                  <mat-option value="">None</mat-option>
                  @for (secret of sftpPasswordSecretReferences; track secret) {
                    <mat-option [value]="secret.reference">
                      {{ secret.name }}
                    </mat-option>
                  }
                </mat-select>
                <mat-hint>{{ sftpSecretPrefixes.password }}</mat-hint>
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Private key secret reference</mat-label>
                <mat-select formControlName="privateKeySecretKey">
                  <mat-option value="">None</mat-option>
                  @for (secret of sftpPrivateKeySecretReferences; track secret) {
                    <mat-option [value]="secret.reference">
                      {{ secret.name }}
                    </mat-option>
                  }
                </mat-select>
                <mat-hint>{{ sftpSecretPrefixes.private_key }}</mat-hint>
              </mat-form-field>
              <button mat-stroked-button type="button" (click)="addSftpSecret('password', 'passwordSecretKey')">
                Add password secret
              </button>
              <button mat-stroked-button type="button" (click)="addSftpSecret('private_key', 'privateKeySecretKey')">
                Add private key secret
              </button>
              <mat-form-field appearance="outline">
                <mat-label>File name prefix</mat-label>
                <input matInput formControlName="fileNamePrefix" />
              </mat-form-field>
              <div class="toggle-row compact">
                <mat-slide-toggle formControlName="pgpEnabled">PGP enabled</mat-slide-toggle>
                <p>Never display stored secret values. Use configured references instead.</p>
              </div>
              @if (reportForm.get('deliveryConfig.pgpEnabled')?.value) {
                <mat-form-field appearance="outline">
                  <mat-label>PGP public key secret reference</mat-label>
                  <mat-select formControlName="publicKeySecretKey">
                    <mat-option value="">None</mat-option>
                    @for (secret of sftpPgpPublicKeySecretReferences; track secret) {
                      <mat-option [value]="secret.reference">
                        {{ secret.name }}
                      </mat-option>
                    }
                  </mat-select>
                  <mat-hint>{{ sftpSecretPrefixes.pgp_public_key }}</mat-hint>
                </mat-form-field>
              }
              @if (reportForm.get('deliveryConfig.pgpEnabled')?.value) {
                <button
                  mat-stroked-button
                  type="button"
                  (click)="addSftpSecret('pgp_public_key', 'publicKeySecretKey')"
                  >
                  Add PGP public key secret
                </button>
              }
            </div>
          }
          @if (reportForm.get('deliveryChannel')?.value !== 'ftp') {
            <div class="form-grid two-up" [formGroup]="deliveryConfigGroup">
              <mat-form-field appearance="outline">
                <mat-label>To</mat-label>
                <textarea matInput rows="2" formControlName="toText" placeholder="recipient@example.com, second@example.com"></textarea>
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>CC</mat-label>
                <input matInput formControlName="ccText" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>BCC</mat-label>
                <input matInput formControlName="bccText" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Subject template</mat-label>
                <input matInput formControlName="subjectTemplate" />
              </mat-form-field>
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Body template</mat-label>
                <textarea matInput rows="4" formControlName="bodyTemplate"></textarea>
              </mat-form-field>
            </div>
          }
        </article>
        <article class="panel">
          <div class="panel-header">
            <span class="panel-step">5</span>
            <div>
              <h3>Schedule</h3>
              @if (!isTeamMembersRuntimeSnapshot) {
                <p>Choose the time zone, cadence, and reporting window.</p>
              }
              @if (isTeamMembersRuntimeSnapshot) {
                <p>Choose the time zone and cadence for the membership snapshot.</p>
              }
            </div>
          </div>
          <div class="form-grid two-up">
            <mat-form-field appearance="outline">
              <mat-label>Time zone</mat-label>
              <input matInput formControlName="timezone" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Frequency</mat-label>
              <mat-select formControlName="scheduleType">
                @for (type of scheduleTypes; track type) {
                  <mat-option [value]="type.value">{{ type.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
          <div class="form-grid two-up" [formGroup]="scheduleConfigGroup">
            @if (isWeeklySchedule) {
              <mat-form-field appearance="outline">
                <mat-label>Days of week</mat-label>
                <mat-select multiple formControlName="daysOfWeek">
                  @for (day of dayOptions; track day) {
                    <mat-option [value]="day.value">{{ day.label }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }
            @if (isMonthlySchedule) {
              <mat-form-field appearance="outline">
                <mat-label>Day of month</mat-label>
                <input matInput type="number" formControlName="dayOfMonth" />
              </mat-form-field>
            }
            <mat-form-field appearance="outline">
              <mat-label>Time of day</mat-label>
              <input matInput type="time" formControlName="time" />
            </mat-form-field>
            @if (!isTeamMembersRuntimeSnapshot) {
              <mat-form-field appearance="outline">
                <mat-label>Window mode</mat-label>
                <mat-select formControlName="windowMode">
                  @for (mode of windowModes; track mode) {
                    <mat-option [value]="mode.value">{{ mode.label }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }
            @if (isTeamMembersRuntimeSnapshot) {
              <div class="info-card">
                <strong>Window mode does not apply</strong>
                <p>Team Members includes memberships active when the report runs. Change as-of mode to Window end to build the membership snapshot at the end of a reporting window.</p>
              </div>
            }
            @if (isRollingWindow) {
              <mat-form-field appearance="outline">
                <mat-label>Number of trailing days</mat-label>
                <input matInput type="number" formControlName="rollingDays" />
              </mat-form-field>
            }
          </div>
        </article>
        <article class="panel">
          <div class="panel-header">
            <span class="panel-step">6</span>
            <div>
              <h3>As-of behavior</h3>
              <p>As-of date controls which historical state is considered true when building the report.</p>
            </div>
          </div>
          <div class="form-grid one-up" [formGroup]="temporalGroup">
            @if (!isSubscriptionCoverageDataset()) {
              <mat-form-field appearance="outline">
                <mat-label>As-of mode</mat-label>
                <mat-select formControlName="asOfMode" [disabled]="isTemplateReport">
                  @for (mode of asOfModes; track mode) {
                    <mat-option [value]="mode.value">{{ mode.label }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }
            @if (isTemplateReport) {
              <div class="info-card">
                <strong>Canned templates use run-time as-of mode today</strong>
                <p>The current backend template implementation fixes scheduled runs to run time. Manual reruns can still override the as-of date.</p>
              </div>
            }
            @if (isProductEnrollmentEventsDataset()) {
              <div class="info-card">
                <strong>Product enrollment events are raw history rows</strong>
                <p>Window and as-of settings do not filter these rows by event date. Add Created At or Modified At filters when the report should include only events from a date range.</p>
              </div>
            }
            @if (!isTemplateReport && !isSubscriptionCoverageDataset()) {
              <div class="mode-help">
                @for (mode of asOfModes; track mode) {
                  <div>
                    <strong>{{ mode.label }}</strong>
                    <p>{{ mode.help }}</p>
                  </div>
                }
              </div>
            }
          </div>
        </article>
        <article class="panel preview-panel">
          <div class="panel-header">
            <span class="panel-step">7</span>
            <div>
              <h3>Preview</h3>
              <p>Preview should succeed before the report is activated.</p>
            </div>
          </div>
          <div class="notice-card">
            <mat-slide-toggle
              [checked]="editorIncludeDemoUsers"
              [disabled]="isPreviewLoading"
              (change)="changeEditorDemoPreview($event.checked)">
              Include demo users
            </mat-slide-toggle>
            <p>Applies only to this preview. Other report filters still apply.</p>
          </div>
          @if (isPreviewLoading) {
            <div class="inline-loading">
              <mat-spinner [diameter]="28"></mat-spinner>
              <span>Building preview</span>
            </div>
          }
          @if (previewError) {
            <div class="notice-card error">
              <h4>Preview failed</h4>
              <p>{{ previewError }}</p>
            </div>
          }
          @if (preview && editorSensitivePreviewMode) {
            <div class="notice-card sensitive-preview-notice">
              <div>
                <h4>{{ editorSensitivePreviewMode === 'revealed' ? 'Full SSNs are visible' : 'SSNs are masked' }}</h4>
                @if (editorSensitivePreviewMode === 'masked') {
                  <p>Every full SSN is masked without being decrypted.</p>
                }
                @if (editorSensitivePreviewMode === 'masked' && !sensitivePreviewBusinessPurposes.length) {
                  <p>Full SSN reveal is unavailable because no approved business purposes are configured.</p>
                }
                @if (editorSensitivePreviewMode === 'revealed') {
                  <p>Full SSNs are displayed only for the approved purpose recorded with this audited preview.</p>
                }
              </div>
              @if (editorSensitivePreviewMode === 'masked') {
                <button
                  mat-stroked-button
                  color="warn"
                  type="button"
                  (click)="revealEditorFullSsns()"
                  [disabled]="isPreviewLoading || !sensitivePreviewBusinessPurposes.length"
                  >
                  Reveal full SSNs
                </button>
              }
              @if (editorSensitivePreviewMode === 'revealed') {
                <button
                  mat-stroked-button
                  type="button"
                  (click)="maskEditorSsns()"
                  >
                  Mask SSNs
                </button>
              }
            </div>
          }
          @if (preview) {
            <div class="preview-meta">
              <div>
                <span class="meta-label">Effective scope</span>
                <span>{{ $safeNavigationMigration(reportForm.get('scopeType')?.value) | titlecase }} - {{ getSelectedScopeLabel(reportForm.get('scopeType')?.value || '') }}</span>
              </div>
              <div>
                <span class="meta-label">Row count</span>
                <span>{{ preview.totalCount }}</span>
              </div>
              <div>
                <span class="meta-label">File name</span>
                <span>{{ fileNamePreview }}</span>
              </div>
              <div>
                <span class="meta-label">As-of date</span>
                <span>{{ formatReportDateTime(preview.runContext.asOfAt, preview.runContext.timezone) }}</span>
              </div>
              <div>
                <span class="meta-label">Applied data window start</span>
                <span>{{ formatReportDateTime(preview.runContext.windowStart, preview.runContext.timezone) }}</span>
              </div>
              <div>
                <span class="meta-label">Applied data window end</span>
                <span>{{ formatReportDateTime(preview.runContext.windowEnd, preview.runContext.timezone) }}</span>
              </div>
            </div>
            @if (preview.warnings.length) {
              <div class="warning-list">
                <h4>Warnings</h4>
                <ul>
                  @for (warning of preview.warnings; track warning) {
                    <li>{{ warning }}</li>
                  }
                </ul>
              </div>
            }
            @if (!preview.sampleRows.length) {
              <div class="empty-inline-state">
                <h4>No preview rows</h4>
                <p>Adjust the applied data window, scope, filters, or as-of behavior to find matching rows.</p>
              </div>
            }
            @if (preview.sampleRows.length) {
              <div class="table-shell" [class.preview-scroll-shell]="shouldUseScrollablePreview(preview)">
                <table class="reports-table preview-table">
                  <thead>
                    <tr>
                      @for (header of getColumnPreviewHeaders(preview); track header) {
                        <th>
                          <button type="button" class="preview-sort-button" (click)="togglePreviewSort('editor', header)">
                            <span>{{ getDisplayColumnHeader(header) }}</span>
                            @if (getPreviewSortDirection('editor', header) === 'asc') {
                              <span class="preview-sort-indicator">arrow_upward</span>
                            }
                            @if (getPreviewSortDirection('editor', header) === 'desc') {
                              <span class="preview-sort-indicator">arrow_downward</span>
                            }
                          </button>
                        </th>
                      }
                    </tr>
                  </thead>
                  <tbody>
                    @for (row of getSortedPreviewRows(preview, 'editor'); track row) {
                      <tr>
                        @for (header of getColumnPreviewHeaders(preview); track header) {
                          <td
                            [class.preview-cell-multiline]="isPreviewCellMultiline(header, getPreviewCellValue(preview, row, header))"
                            >
                            {{ getPreviewCellText(header, getPreviewCellValue(preview, row, header)) }}
                          </td>
                        }
                      </tr>
                    }
                  </tbody>
                  @if (shouldShowPreviewTotals(preview, 'editor')) {
                    <tfoot>
                      <tr class="preview-total-row">
                        @for (header of getColumnPreviewHeaders(preview); track header) {
                          <td
                            [class.preview-total-label]="isPreviewTotalLabelColumn(preview, 'editor', header)"
                            [class.preview-total-value]="isPreviewTotalValueColumn(preview, 'editor', header)"
                            >
                            {{ getPreviewTotalCellText(preview, 'editor', header) }}
                          </td>
                        }
                      </tr>
                    </tfoot>
                  }
                </table>
              </div>
            }
            @if (isPreviewTotalFallbackSampleOnly(preview, 'editor')) {
              <div class="notice-card warning">
                <h4>Preview totals use sample rows</h4>
                <p>The final downloaded report can total the full file when the backend returns report totals.</p>
              </div>
            }
          }
        </article>
      </form>
      @if (validationMessages.length) {
        <div class="validation-panel">
          <h3>Review this report before saving</h3>
          <ul>
            @for (message of validationMessages; track message) {
              <li>{{ message }}</li>
            }
          </ul>
        </div>
      }
      <footer class="editor-footer">
        <button mat-button type="button" (click)="cancelEditor()">Cancel</button>
        <button mat-stroked-button type="button" (click)="previewDraft()" [disabled]="isPreviewLoading || isSaving">Preview</button>
        @if (currentPermissions.runManual) {
          <button
            mat-stroked-button
            type="button"
            (click)="sendNowFromEditor()"
            [disabled]="isSaving || isPreviewLoading"
            >
            Send now
          </button>
        }
        <button mat-stroked-button type="button" (click)="saveDraft()" [disabled]="isSaving">Save draft</button>
        <button mat-raised-button color="primary" type="button" (click)="activateReport()" [disabled]="isSaving || !canActivate">
          Activate
        </button>
      </footer>
    </section>
  }
  @if (viewMode === 'detail') {
    <section class="detail-shell">
      @if (isDetailLoading) {
        <section class="inline-loading">
          <mat-spinner [diameter]="28"></mat-spinner>
          <span>Loading report detail</span>
        </section>
      }
      @if (!isDetailLoading && detailReport) {
        <div class="detail-header">
          <div>
            <div class="detail-kicker">{{ getReportTypeLabel(detailReport.reportType) }}</div>
            <h2>{{ detailReport.name }}</h2>
            <p>{{ detailReport.description || 'No description provided.' }}</p>
          </div>
          <div class="detail-actions">
            @if (currentPermissions.edit) {
              <button mat-stroked-button type="button" (click)="editCurrentReport()">Edit</button>
            }
            @if (currentPermissions.pauseResume) {
              <button mat-stroked-button type="button" (click)="togglePauseResume(detailReport)">
                {{ detailReport.status === 'active' ? 'Pause' : 'Resume' }}
              </button>
            }
            @if (currentPermissions.runManual) {
              <button mat-stroked-button type="button" (click)="runNow(detailReport)">Run now</button>
            }
            @if (currentPermissions.rerunWithAsOf) {
              <button mat-stroked-button type="button" (click)="rerunWithAsOf(detailReport, selectedRun)">
                Rerun with as-of date
              </button>
            }
            @if (currentPermissions.delete) {
              <button mat-stroked-button color="warn" type="button" (click)="confirmDeleteReport(detailReport)">
                Archive
              </button>
            }
            @if (latestArtifactRun) {
              <button mat-raised-button color="primary" type="button" (click)="downloadArtifact(latestArtifactRun)">
                Download latest artifact
              </button>
            }
          </div>
        </div>
        <div class="detail-grid">
          <article class="panel">
            <div class="panel-header simple">
              <div>
                <h3>Configuration summary</h3>
                <p>Read-only overview of the saved report configuration.</p>
              </div>
            </div>
            <div class="summary-grid">
              <div>
                <span class="meta-label">Scope</span>
                <strong>{{ getScopeLabel(detailReport) }}</strong>
              </div>
              <div>
                <span class="meta-label">Format</span>
                <strong>{{ getFormatLabel(detailReport.outputFormat) }}</strong>
              </div>
              <div>
                <span class="meta-label">Delivery</span>
                <strong>{{ getDeliveryLabel(detailReport.deliveryChannel) }}</strong>
              </div>
              <div>
                <span class="meta-label">Schedule</span>
                <strong>{{ getScheduleSummary(detailReport) }}</strong>
              </div>
              <div>
                <span class="meta-label">Status</span>
                <strong>{{ detailReport.status }}</strong>
              </div>
              <div>
                <span class="meta-label">Next run</span>
                <strong>{{ formatReportDateTime(detailReport.nextRunAt, detailReport.timezone) }}</strong>
              </div>
            </div>
            <div class="definition-summary">
              <div>
                <span class="meta-label">Output columns</span>
                <ul>
                  @for (column of getDetailOutputColumns(detailReport); track column) {
                    <li>{{ getDisplayColumnHeader(column) }}</li>
                  }
                  @if (!getDetailOutputColumns(detailReport).length) {
                    <li>No output columns configured.</li>
                  }
                </ul>
              </div>
              <div>
                <span class="meta-label">Filters</span>
                <ul>
                  @for (filter of detailReport.definitionJson?.filters; track filter) {
                    <li>
                      {{ getFieldLabel(filter.field) }} {{ filter.operator }} {{ filter.value }}
                    </li>
                  }
                  @if (!detailReport.definitionJson?.filters?.length) {
                    <li>No filters configured.</li>
                  }
                </ul>
              </div>
              <div>
                <span class="meta-label">Sort</span>
                <ul>
                  <!-- Derived read-only summary; position represents sort priority. -->
                  @for (sort of getDetailSortRules(detailReport); track $index) {
                    <li>
                      {{ getFieldLabel(sort.field) }} - {{ getSortDirectionLabel(sort.direction) }}
                    </li>
                  }
                  @if (!getDetailSortRules(detailReport).length) {
                    <li>No sort configured.</li>
                  }
                </ul>
              </div>
            </div>
          </article>
          <article class="panel">
            <div class="panel-header simple">
              <div>
                <h3>Delivery details</h3>
                <p>Recipient or SFTP configuration summary without secret contents.</p>
              </div>
            </div>
            @if (detailReport.deliveryChannel === 'ftp') {
              <div class="summary-grid">
                <div>
                  <span class="meta-label">Host</span>
                  <strong>{{ detailReport.deliveryConfig.host || '--' }}</strong>
                </div>
                <div>
                  <span class="meta-label">Username</span>
                  <strong>{{ detailReport.deliveryConfig.username || '--' }}</strong>
                </div>
                <div>
                  <span class="meta-label">Folder</span>
                  <strong>{{ detailReport.deliveryConfig.folder || '--' }}</strong>
                </div>
                <div>
                  <span class="meta-label">Password secret</span>
                  <strong>{{ detailReport.deliveryConfig.passwordSecretKey ? 'Configured' : 'Not configured' }}</strong>
                </div>
                <div>
                  <span class="meta-label">Private key secret</span>
                  <strong>{{ detailReport.deliveryConfig.privateKeySecretKey ? 'Configured' : 'Not configured' }}</strong>
                </div>
                <div>
                  <span class="meta-label">PGP</span>
                  <strong>{{ detailReport.deliveryConfig.pgp?.enabled ? 'Enabled' : 'Disabled' }}</strong>
                </div>
              </div>
            } @else {
              <div class="summary-grid">
                <div>
                  <span class="meta-label">To</span>
                  <strong>{{ (detailReport.deliveryConfig.to || []).join(', ') || '--' }}</strong>
                </div>
                <div>
                  <span class="meta-label">CC</span>
                  <strong>{{ (detailReport.deliveryConfig.cc || []).join(', ') || '--' }}</strong>
                </div>
                <div>
                  <span class="meta-label">BCC</span>
                  <strong>{{ (detailReport.deliveryConfig.bcc || []).join(', ') || '--' }}</strong>
                </div>
                <div>
                  <span class="meta-label">Provider</span>
                  <strong>{{ detailReport.deliveryConfig.provider || 'Standard email' }}</strong>
                </div>
              </div>
              <div class="definition-summary">
                <div>
                  <span class="meta-label">Subject</span>
                  <p>{{ detailReport.deliveryConfig.subjectTemplate || '--' }}</p>
                </div>
                <div>
                  <span class="meta-label">Body</span>
                  <p>{{ detailReport.deliveryConfig.bodyTemplate || '--' }}</p>
                </div>
              </div>
            }
          </article>
          <article class="panel">
            <div class="panel-header simple">
              <div>
                <h3>Latest preview snapshot</h3>
                <p>Preview output columns, sample rows, warnings, and final file name.</p>
              </div>
            </div>
            <div class="notice-card">
              <mat-slide-toggle
                [checked]="detailIncludeDemoUsers"
                [disabled]="isDetailLoading || isDetailPreviewLoading"
                (change)="changeDetailDemoPreview($event.checked)">
                Include demo users
              </mat-slide-toggle>
              <p>Applies only to this preview. Other report filters still apply.</p>
            </div>
            @if (isDetailPreviewLoading) {
              <div class="inline-loading detail-preview-loading">
                <mat-spinner [diameter]="28"></mat-spinner>
                <span>Building preview</span>
              </div>
            }
            @if (detailPreviewError) {
              <div class="notice-card error">
                <h4>Preview failed</h4>
                <p>{{ detailPreviewError }}</p>
              </div>
            }
            @if (!detailPreview && detailReportIsSensitive && !isDetailPreviewLoading) {
              <div class="empty-inline-state">
                <h4>Preview with SSNs masked</h4>
                <p>Full SSNs remain encrypted and are masked in the preview. Approval context is required only when revealing them.</p>
                <button mat-raised-button color="primary" type="button" (click)="previewDetailSensitiveMasked()">
                  Preview with SSNs masked
                </button>
              </div>
            }
            @if (!detailPreview && !detailReportIsSensitive && !isDetailPreviewLoading) {
              <div class="empty-inline-state">
                <h4>No preview available</h4>
                <p>Open the editor and run a preview to inspect current output columns and rows.</p>
              </div>
            }
            @if (detailPreview) {
              @if (detailSensitivePreviewMode) {
                <div class="notice-card sensitive-preview-notice">
                  <div>
                    <h4>{{ detailSensitivePreviewMode === 'revealed' ? 'Full SSNs are visible' : 'SSNs are masked' }}</h4>
                    @if (detailSensitivePreviewMode === 'masked') {
                      <p>Every full SSN is masked without being decrypted.</p>
                    }
                    @if (detailSensitivePreviewMode === 'masked' && !sensitivePreviewBusinessPurposes.length) {
                      <p>Full SSN reveal is unavailable because no approved business purposes are configured.</p>
                    }
                    @if (detailSensitivePreviewMode === 'revealed') {
                      <p>Full SSNs are displayed only for the approved purpose recorded with this audited preview.</p>
                    }
                  </div>
                  @if (detailSensitivePreviewMode === 'masked') {
                    <button
                      mat-stroked-button
                      color="warn"
                      type="button"
                      (click)="revealDetailFullSsns()"
                      [disabled]="isDetailPreviewLoading || !sensitivePreviewBusinessPurposes.length"
                      >
                      Reveal full SSNs
                    </button>
                  }
                  @if (detailSensitivePreviewMode === 'revealed') {
                    <button
                      mat-stroked-button
                      type="button"
                      (click)="maskDetailSsns()"
                      >
                      Mask SSNs
                    </button>
                  }
                </div>
              }
              <div class="preview-meta">
                <div>
                  <span class="meta-label">Sample row count</span>
                  <span>{{ detailPreview.sampleRows.length }}</span>
                </div>
                <div>
                  <span class="meta-label">Total rows</span>
                  <span>{{ detailPreview.totalCount }}</span>
                </div>
                <div>
                  <span class="meta-label">Final file name</span>
                  <span>{{ detailReport.fileNamePattern || fileNamePreview }}</span>
                </div>
                <div>
                  <span class="meta-label">As-of date</span>
                  <span>{{ formatReportDateTime(detailPreview.runContext.asOfAt, detailPreview.runContext.timezone) }}</span>
                </div>
                <div>
                  <span class="meta-label">Applied data window start</span>
                  <span>{{ formatReportDateTime(detailPreview.runContext.windowStart, detailPreview.runContext.timezone) }}</span>
                </div>
                <div>
                  <span class="meta-label">Applied data window end</span>
                  <span>{{ formatReportDateTime(detailPreview.runContext.windowEnd, detailPreview.runContext.timezone) }}</span>
                </div>
              </div>
              @if (detailPreview.warnings.length) {
                <div class="warning-list">
                  <h4>Warnings</h4>
                  <ul>
                    @for (warning of detailPreview.warnings; track warning) {
                      <li>{{ warning }}</li>
                    }
                  </ul>
                </div>
              }
              @if (detailPreview.sampleRows.length) {
                <div class="table-shell" [class.preview-scroll-shell]="shouldUseScrollablePreview(detailPreview)">
                  <table class="reports-table preview-table">
                    <thead>
                      <tr>
                        @for (header of getColumnPreviewHeaders(detailPreview); track header) {
                          <th>
                            <button type="button" class="preview-sort-button" (click)="togglePreviewSort('detail', header)">
                              <span>{{ getDisplayColumnHeader(header) }}</span>
                              @if (getPreviewSortDirection('detail', header) === 'asc') {
                                <span class="preview-sort-indicator">arrow_upward</span>
                              }
                              @if (getPreviewSortDirection('detail', header) === 'desc') {
                                <span class="preview-sort-indicator">arrow_downward</span>
                              }
                            </button>
                          </th>
                        }
                      </tr>
                    </thead>
                    <tbody>
                      @for (row of getSortedPreviewRows(detailPreview, 'detail'); track row) {
                        <tr>
                          @for (header of getColumnPreviewHeaders(detailPreview); track header) {
                            <td
                              [class.preview-cell-multiline]="isPreviewCellMultiline(header, getPreviewCellValue(detailPreview, row, header))"
                              >
                              {{ getPreviewCellText(header, getPreviewCellValue(detailPreview, row, header)) }}
                            </td>
                          }
                        </tr>
                      }
                    </tbody>
                    @if (shouldShowPreviewTotals(detailPreview, 'detail')) {
                      <tfoot>
                        <tr class="preview-total-row">
                          @for (header of getColumnPreviewHeaders(detailPreview); track header) {
                            <td
                              [class.preview-total-label]="isPreviewTotalLabelColumn(detailPreview, 'detail', header)"
                              [class.preview-total-value]="isPreviewTotalValueColumn(detailPreview, 'detail', header)"
                              >
                              {{ getPreviewTotalCellText(detailPreview, 'detail', header) }}
                            </td>
                          }
                        </tr>
                      </tfoot>
                    }
                  </table>
                </div>
              }
              @if (isPreviewTotalFallbackSampleOnly(detailPreview, 'detail')) {
                <div class="notice-card warning">
                  <h4>Preview totals use sample rows</h4>
                  <p>The final downloaded report can total the full file when the backend returns report totals.</p>
                </div>
              }
            }
          </article>
          <article class="panel">
            <div class="panel-header simple">
              <div>
                <h3>Run history</h3>
                <p>Review historical runs, delivery results, artifacts, and error details.</p>
              </div>
            </div>
            @if (!reportRuns.length) {
              <div class="empty-inline-state">
                <h4>No runs yet</h4>
                <p>Use Run now or activate the schedule to create the first run record.</p>
              </div>
            }
            @if (reportRuns.length) {
              <div class="table-shell">
                <table class="reports-table">
                  <thead>
                    <tr>
                      <th>Run time</th>
                      <th>Trigger type</th>
                      <th>Result</th>
                      <th>Rows</th>
                      <th>Format</th>
                      <th>As-of date</th>
                      <th>Window</th>
                      <th>Delivery</th>
                      <th class="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (run of reportRuns; track trackById($index, run)) {
                      <tr [class.selected-row]="selectedRun?.id === run.id">
                        <td>{{ formatReportDateTime(run.startedAt || run.createdAt, detailReport.timezone) }}</td>
                        <td>{{ run.triggerType }}</td>
                        <td>{{ run.status }}</td>
                        <td>{{ run.rowsGenerated ?? '--' }}</td>
                        <td>{{ run.outputFormat || '--' }}</td>
                        <td>{{ formatReportDateTime(run.asOfAt, detailReport.timezone) }}</td>
                        <td>{{ formatReportDateRange(run.windowStart, run.windowEnd, detailReport.timezone) }}</td>
                        <td>{{ run.deliveryChannel || '--' }}</td>
                        <td class="actions-col">
                          <button mat-button type="button" (click)="selectRun(run)">Details</button>
                          @if (run.artifactUrl) {
                            <button mat-button type="button" (click)="downloadArtifact(run)">Download</button>
                          }
                          <button mat-button type="button" (click)="retryRun(run)">Retry</button>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
            @if (selectedRun) {
              <div class="run-detail">
                <div class="run-detail-header">
                  <h4>Run detail</h4>
                  <div class="run-detail-actions">
                    @if (selectedRun.artifactUrl) {
                      <button mat-stroked-button type="button" (click)="downloadArtifact(selectedRun)">
                        Download artifact
                      </button>
                    }
                    <button mat-stroked-button type="button" (click)="retryRun(selectedRun)">Retry run</button>
                    <button mat-stroked-button type="button" (click)="rerunWithAsOf(detailReport, selectedRun)">Rerun with new as-of date</button>
                  </div>
                </div>
                <div class="summary-grid">
                  <div>
                    <span class="meta-label">Run status</span>
                    <strong>{{ selectedRun.status }}</strong>
                  </div>
                  <div>
                    <span class="meta-label">Started at</span>
                    <strong>{{ formatReportDateTime(selectedRun.startedAt, detailReport.timezone) }}</strong>
                  </div>
                  <div>
                    <span class="meta-label">Finished at</span>
                    <strong>{{ formatReportDateTime(selectedRun.finishedAt, detailReport.timezone) }}</strong>
                  </div>
                  <div>
                    <span class="meta-label">Rows generated</span>
                    <strong>{{ selectedRun.rowsGenerated ?? '--' }}</strong>
                  </div>
                  <div>
                    <span class="meta-label">Artifact</span>
                    <strong>{{ selectedRun.fileName || '--' }}</strong>
                  </div>
                  <div>
                    <span class="meta-label">Error details</span>
                    <strong>{{ selectedRun.errorMessage || '--' }}</strong>
                  </div>
                </div>
                <div class="detail-json-grid">
                  <div>
                    <span class="meta-label">Config snapshot summary</span>
                    <pre>{{ selectedRun.definitionSnapshot | json }}</pre>
                  </div>
                  <div>
                    <span class="meta-label">Delivery result</span>
                    <pre>{{ selectedRun.deliveryResult | json }}</pre>
                  </div>
                </div>
              </div>
            }
          </article>
        </div>
      }
    </section>
  }
}
</div>

<mat-menu #reportActionsMenu="matMenu">
  <ng-template matMenuContent let-report="report">
    <button mat-menu-item type="button" (click)="openReport(report)">
      <mat-icon>open_in_new</mat-icon>
      <span>Open</span>
    </button>
    @if (currentPermissions.pauseResume) {
      <button mat-menu-item type="button" (click)="togglePauseResume(report)">
        <mat-icon>{{ report.status === 'active' ? 'pause' : 'play_arrow' }}</mat-icon>
        <span>{{ report.status === 'active' ? 'Pause' : 'Resume' }}</span>
      </button>
    }
    @if (currentPermissions.runManual) {
      <button mat-menu-item type="button" (click)="runNow(report)">
        <mat-icon>play_circle</mat-icon>
        <span>Run now</span>
      </button>
    }
    @if (currentPermissions.edit) {
      <button mat-menu-item type="button" (click)="duplicateReport(report)">
        <mat-icon>content_copy</mat-icon>
        <span>Duplicate</span>
      </button>
    }
    @if (currentPermissions.delete) {
      <button mat-menu-item type="button" (click)="confirmDeleteReport(report, $event)">
        <mat-icon>delete</mat-icon>
        <span>Archive</span>
      </button>
    }
  </ng-template>
</mat-menu>
